import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Form } from 'foundations-components/transitional/components/Form';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Select } from 'foundations-components/components/Select';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Flex } from 'foundations-components/components/Flex';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import usePageInteractionTracking from '../../hooks/usePageInteractionTracking';
import { INTERACTION_SECTIONS, INTERACTION_TARGETS } from 'payments-checkout-lib/constants/PageInteractionTracking';
import { ACH_METHOD, PADS_METHOD } from 'payments-checkout-lib/constants/PaymentElement';
import { setErrorMessages, getShowAllErrors } from '../../state/errorMessagesSlice';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';
import useWindowSize from '../../hooks/useWindowSize';
import { ROW_BREAKPOINT } from 'payments-checkout-lib/constants/Sizes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getAccountHolderTypeOptions = () => [{
  text: I18n.text('payments.achPaymentInfo.accountHolderType.options.individual'),
  value: 'INDIVIDUAL'
}, {
  text: I18n.text('payments.achPaymentInfo.accountHolderType.options.company'),
  value: 'COMPANY'
}];
const getTransactionTypeOptions = () => [{
  text: I18n.text('payments.padsPaymentInfo.transactionType.options.business'),
  value: 'BUSINESS'
}, {
  text: I18n.text('payments.padsPaymentInfo.transactionType.options.personal'),
  value: 'PERSONAL'
}];
const BankBillingInfo = ({
  stripePaymentMethod
}) => {
  const {
    formState,
    dispatch
  } = usePaymentFormContext();
  const {
    trackFirstFormFieldInteraction
  } = usePageInteractionTracking();
  const reduxDispatch = useDispatch();
  const showAllErrors = useSelector(getShowAllErrors);
  const disabled = formState.get('isCheckoutSubmitPending');
  const accountHolderName = formState.getIn(['bankInfo', 'accountHolderName']);
  const accountHolderType = formState.getIn(['bankInfo', 'accountHolderType']);
  const transactionType = formState.getIn(['bankInfo', 'transactionType']);
  const [hasAccountHolderNameBeenBlurred, setHasAccountHolderNameBeenBlurred] = useState(false);
  const isError = (hasAccountHolderNameBeenBlurred || showAllErrors) && !accountHolderName;
  useEffect(() => {
    reduxDispatch(setErrorMessages({
      emptyAccountHolderName: {
        isError
      }
    }));
  }, [isError, reduxDispatch]);
  const errorMessage = useGetErrorMessagesAndTranslations().get('emptyAccountHolderName');
  const handleBankInfoFieldChange = useCallback((fieldName, value) => {
    trackFirstFormFieldInteraction({
      section: INTERACTION_SECTIONS.bankInfo,
      target: INTERACTION_TARGETS[fieldName]
    });
    dispatch({
      type: 'setBankInfoField',
      data: {
        name: fieldName,
        value
      }
    });
  }, [dispatch, trackFirstFormFieldInteraction]);
  const {
    width
  } = useWindowSize();
  const isRow = width >= ROW_BREAKPOINT;
  if (stripePaymentMethod === ACH_METHOD) {
    return /*#__PURE__*/_jsx("span", {
      "data-test-id": "bank-billing-info",
      children: /*#__PURE__*/_jsx(Form, {
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: isRow ? 'row' : 'column',
          gap: "200",
          inlineSize: "100%",
          children: [/*#__PURE__*/_jsx(Flex, {
            flexBasis: "50%",
            children: /*#__PURE__*/_jsx(FormControl, {
              inlineSize: "100%",
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "payments.achPaymentInfo.accountHolderName"
              }),
              required: true,
              error: isError,
              validationMessage: isError && errorMessage,
              children: /*#__PURE__*/_jsx(TextInput, {
                inlineSize: "100%",
                autoComplete: "name",
                "data-test-id": "account-holder-name-input",
                value: accountHolderName,
                onChange: event => handleBankInfoFieldChange('accountHolderName', event.target.value),
                placeholder: I18n.text('payments.achPaymentInfo.accountHolderNamePlaceholder'),
                disabled: disabled,
                use: "on-dark",
                onBlur: () => setHasAccountHolderNameBeenBlurred(true)
              })
            })
          }), /*#__PURE__*/_jsx(Flex, {
            flexBasis: "50%",
            children: /*#__PURE__*/_jsx(FormControl, {
              inlineSize: "100%",
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "payments.achPaymentInfo.accountHolderType.label"
              }),
              required: true,
              children: /*#__PURE__*/_jsx(Select, {
                "data-test-id": "account-type-input",
                selectedValues: [accountHolderType],
                onSelectedOptionChange: ({
                  value
                }) => handleBankInfoFieldChange('accountHolderType', value),
                options: getAccountHolderTypeOptions(),
                disabled: disabled,
                referenceVariant: "on-dark"
              })
            })
          })]
        })
      })
    });
  } else if (stripePaymentMethod === PADS_METHOD) {
    return /*#__PURE__*/_jsx("span", {
      "data-test-id": "bank-billing-info",
      children: /*#__PURE__*/_jsx(Form, {
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: isRow ? 'row' : 'column',
          gap: "200",
          inlineSize: "100%",
          children: [/*#__PURE__*/_jsx(Flex, {
            flexBasis: "50%",
            children: /*#__PURE__*/_jsx(FormControl, {
              inlineSize: "100%",
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "payments.padsPaymentInfo.accountHolderName"
              }),
              required: true,
              error: isError,
              validationMessage: isError && errorMessage,
              children: /*#__PURE__*/_jsx(TextInput, {
                inlineSize: "100%",
                autoComplete: "name",
                "data-test-id": "account-holder-name-input",
                value: accountHolderName,
                onChange: event => handleBankInfoFieldChange('accountHolderName', event.target.value),
                placeholder: I18n.text('payments.padsPaymentInfo.accountHolderNamePlaceholder'),
                disabled: disabled,
                use: "on-dark",
                onBlur: () => setHasAccountHolderNameBeenBlurred(true)
              })
            })
          }), /*#__PURE__*/_jsx(Flex, {
            flexBasis: "50%",
            children: /*#__PURE__*/_jsx(FormControl, {
              inlineSize: "100%",
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "payments.padsPaymentInfo.transactionType.label"
              }),
              required: true,
              children: /*#__PURE__*/_jsx(Select, {
                "data-test-id": "transaction-type-input",
                selectedValues: [transactionType],
                onSelectedOptionChange: ({
                  value
                }) => handleBankInfoFieldChange('transactionType', value),
                options: getTransactionTypeOptions(),
                disabled: disabled,
                referenceVariant: "on-dark"
              })
            })
          })]
        })
      })
    });
  }
  return null;
};
export default BankBillingInfo;